import { agentExists } from "helpers/agent";
import { mailboxExists } from "helpers/mailboxes";
import { tagExists } from "helpers/tags";
import { teamExists } from "helpers/team";
import { isEmail } from "helpers/validate";
import { TicketPriority, TicketStatus } from "interfaces/entities/ticket";
import { IAgent } from "store/entities/agents";
import { ICannedResponse } from "store/entities/canned-responses";
import { cannedExists } from "store/entities/canned-responses/helpers";
import { IMacroAction } from "store/entities/macros/interfaces";
import { IMailbox } from "store/entities/mailboxes/interfaces";
import {
  IMessageBasedAction,
  IRuleAction,
  IRuleActionAssign,
  IRuleActionSpam,
  IRuleTrigger,
} from "store/entities/rules/interfaces";
import { ITag } from "store/entities/tags";
import { ITeam } from "store/entities/teams";

import { TRIGGER_ANY_AGENT_OPTION_KEY, TRIGGER_UNASSIGNED_OPTION_KEY } from "../workflows/components/triggers/utils";

const ERROR_MESSAGE = "Make sure the condition is added correctly.";

const validate = (isValid: boolean): string | undefined => (!isValid ? ERROR_MESSAGE : undefined);

const nonEmpty = <T extends { length: number }>(value: T): string | undefined => validate(value.length > 0);

const validPriority = (value: number): string | undefined => validate(value in TicketPriority);

const validTags = (tagIDs: string[], allTags: ITag[]): string | undefined => {
  const everyTagExists = tagIDs.every(tagID => tagExists(tagID, allTags));
  return nonEmpty(tagIDs) || validate(everyTagExists);
};

const validFollowers = (followerIDs: string[], allFollowers: IAgent[]): string | undefined => {
  const everyFollowerExists = followerIDs.every(followerID => agentExists(allFollowers, followerID));
  return nonEmpty(followerIDs) || validate(everyFollowerExists);
};

const validStatus = (value: TicketStatus): string | undefined =>
  validate(
    [TicketStatus.Open, TicketStatus.Pending, TicketStatus.OnHold, TicketStatus.Solved, TicketStatus.Closed].includes(
      value
    )
  );

// https://livechatinc.atlassian.net/wiki/x/BQAoy
const validAgentChangedArray = (agentIDs: string[] | undefined, allAgents: IAgent[]): string | undefined => {
  if (!agentIDs) {
    return undefined;
  }

  if (agentIDs.length === 0) {
    return ERROR_MESSAGE;
  }

  return validate(
    agentIDs.every(
      agentID =>
        agentID === TRIGGER_ANY_AGENT_OPTION_KEY ||
        agentID === TRIGGER_UNASSIGNED_OPTION_KEY ||
        agentExists(allAgents, agentID)
    )
  );
};

export const validTeam = (teamID: string | null | undefined, allTeams: ITeam[]): string | undefined => {
  return validate(teamID === null || Boolean(teamID && teamExists(allTeams, teamID)));
};

export const validAgent = (
  agentID: string | null | undefined,
  randomAgent: boolean | undefined,
  roundRobin: boolean | undefined,
  allAgents: IAgent[]
): string | undefined => {
  const isRandom = randomAgent;
  const isRoundRobin = roundRobin;
  const isUnassigned = agentID === null;
  const isExistingAgentSelected = Boolean(agentID && !isUnassigned && agentExists(allAgents, agentID));

  return validate(
    !!(isRoundRobin && !isExistingAgentSelected) ||
      (isRandom && !isExistingAgentSelected) ||
      isUnassigned ||
      isExistingAgentSelected
  );
};

const validAssignment = (action: IRuleActionAssign, allTeams: ITeam[], allAgents: IAgent[]): string | undefined => {
  const { teamID, agentID, randomAgent, roundRobin } = action;
  const isTeamValid = validTeam(teamID, allTeams);
  const isAgentValid = validAgent(agentID, randomAgent, roundRobin, allAgents);

  return validate(Boolean(!isTeamValid && !isAgentValid));
};

export const validCannedResponse = (
  cannedResponseID: string | undefined,
  allCannedResponses: ICannedResponse[]
): string | undefined => {
  return validate(Boolean(cannedResponseID && cannedExists(cannedResponseID, allCannedResponses)));
};

const validMessage = (action: IMessageBasedAction, allCannedResponses: ICannedResponse[]): string | undefined => {
  if (action.template) {
    return nonEmpty(action.template.text);
  } else if (action.cannedResponseID) {
    return validCannedResponse(action.cannedResponseID, allCannedResponses);
  } else {
    return ERROR_MESSAGE;
  }
};

const validSpam = (action: IRuleActionSpam): string | undefined => validate(typeof action.spam === "boolean");

export const validateAction = (
  action: IMacroAction | IRuleAction | Record<string, never>,
  allTeams: ITeam[],
  allAgents: IAgent[],
  allCannedResponses: ICannedResponse[],
  allTags: ITag[]
): string | undefined => {
  switch (action.type) {
    case "priority":
      return validPriority(action.priority);
    case "spam":
      return validSpam(action);
    case "addTags":
    case "removeTags":
      return validTags(action.tagIDs, allTags);
    case "status":
      return validStatus(action.status);
    case "addFollowers":
    case "removeFollowers":
      return validFollowers(action.followers, allAgents);
    case "assign":
      return validAssignment(action, allTeams, allAgents);
    case "message":
    case "sendRatingRequest":
    case "sendFollowUp":
      return validMessage(action, allCannedResponses);
    default:
      return ERROR_MESSAGE;
  }
};

export const validateActions = (
  actions: (IMacroAction | IRuleAction | Record<string, never>)[],
  allTeams: ITeam[],
  allAgents: IAgent[],
  allCannedResponses: ICannedResponse[],
  allTags: ITag[]
): (string | undefined)[] =>
  actions.map(action => validateAction(action, allTeams, allAgents, allCannedResponses, allTags));

export const validateTrigger = (
  trigger: IRuleTrigger,
  allTags: ITag[],
  allTeams: ITeam[],
  allAgents: IAgent[],
  allMailboxes: IMailbox[]
): string | undefined => {
  switch (trigger.type) {
    case "detailedSourceType":
    case "status":
    case "priority":
    case "rating":
    case "language":
      return nonEmpty(trigger.in);
    case "sourceMailbox":
      return (
        nonEmpty(trigger.in) ||
        validate(
          trigger.in.every(mailboxID => {
            if (mailboxID === "default") {
              return true;
            }
            return mailboxExists(allMailboxes, mailboxID);
          })
        )
      );
    case "statusChanged":
      // 'undefined' value for newStatusIn or oldStatusIn is valid - means any
      return validate(
        (!trigger.newStatusIn || trigger.newStatusIn.length > 0) &&
          (!trigger.oldStatusIn || trigger.oldStatusIn.length > 0)
      );
    case "priorityChanged":
      // 'undefined' value for newPriorityIn or oldPriorityIn is valid - means any
      return validate(
        (!trigger.newPriorityIn || trigger.newPriorityIn.length > 0) &&
          (!trigger.oldPriorityIn || trigger.oldPriorityIn.length > 0)
      );
    case "ratingChanged":
      return nonEmpty(trigger.newRatingIn);
    case "subject":
    case "newMessage":
    case "newAgentMessage":
      if (trigger.containsIn !== undefined) {
        return nonEmpty(trigger.containsIn);
      }

      if (trigger.containsWholePhraseIn !== undefined) {
        return nonEmpty(trigger.containsWholePhraseIn);
      }

      if (trigger.containsWholePhraseInCI !== undefined) {
        return nonEmpty(trigger.containsWholePhraseInCI);
      }

      if (trigger.containsInCI !== undefined) {
        return nonEmpty(trigger.containsInCI);
      }

      return ERROR_MESSAGE;
    case "tagID":
    case "tagAdded":
    case "tagRemoved":
      return validTags(trigger.in, allTags);

    case "eventCount":
      return nonEmpty(trigger.eventTypeIn);

    case "teamID":
      return nonEmpty(trigger.in) || validate(trigger.in.every(teamID => teamExists(allTeams, teamID)));

    case "teamChanged": {
      // 'undefined' value for newTeamIn or oldTeamIn is valid - means any
      const { newTeamIn, oldTeamIn } = trigger;

      return validate(
        (!newTeamIn || (newTeamIn.length > 0 && newTeamIn.every(teamID => teamExists(allTeams, teamID)))) &&
          (!oldTeamIn || (oldTeamIn.length > 0 && oldTeamIn.every(teamID => teamExists(allTeams, teamID))))
      );
    }
    case "agentID":
    case "followerID":
    case "followerAdded":
    case "followerRemoved":
      return nonEmpty(trigger.in) || validate(trigger.in.every(agentID => agentExists(allAgents, agentID)));

    case "agentChanged": {
      // 'undefined' value for newAgentIn or oldAgentIn is valid - means any
      const { newAgentIn, oldAgentIn } = trigger;

      return validAgentChangedArray(newAgentIn, allAgents) || validAgentChangedArray(oldAgentIn, allAgents);
    }
    case "requesterEmail":
      if (trigger.in !== undefined) {
        return nonEmpty(trigger.in) || validate(trigger.in.every(email => isEmail(email)));
      }

      if (trigger.containsIn !== undefined) {
        return nonEmpty(trigger.containsIn);
      }

      return ERROR_MESSAGE;

    case "secondsPassedSinceEvent":
      return validate(trigger.eq >= 0) || nonEmpty(trigger.eventTypeIn);

    case "isNew":
    case "hasAssignee":
    case "isSpam":
      return undefined;

    default:
      return ERROR_MESSAGE;
  }
};

export const validateTriggers = (
  triggers: IRuleTrigger[],
  allTags: ITag[],
  allTeams: ITeam[],
  allAgents: IAgent[],
  allMailboxes: IMailbox[]
): (string | undefined)[] =>
  triggers.map(trigger => validateTrigger(trigger, allTags, allTeams, allAgents, allMailboxes));
